import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  CheckIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const today = new Date().toISOString().split('T')[0];

const officeCategories = [
  'Rent',
  'Utilities',
  'Internet',
  'Office Supplies',
  'Maintenance',
  'Repairs',
  'Vendor Payment',
  'Tea & Snacks',
  'Cleaning Supplies',
  'Staff Welfare',
  'Miscellaneous'
];

const emptyOfficeExpense = {
  category: '',
  amount: '',
  description: '',
  purpose: '',
  date: today,
  vendor: '',
  receiptNumber: '',
  paymentMethod: 'cash',
  notes: ''
};

const emptyCategory = {
  name: '',
  description: ''
};

const formatDate = (value) => {
  if (!value) return 'N/A';
  return new Date(value).toLocaleDateString();
};

const actorLabel = (person) => {
  if (!person?.name) return 'N/A';
  return person.employeeId ? `${person.name} (${person.employeeId})` : person.name;
};

const cleanPayload = (data) => {
  const payload = {};
  Object.entries(data).forEach(([key, value]) => {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) payload[key] = trimmed;
    } else if (value !== undefined && value !== null && value !== '') {
      payload[key] = value;
    }
  });
  return payload;
};

const ModalShell = ({ title, children, onClose }) => (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
    <div className="relative top-16 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
          &times;
        </button>
      </div>
      {children}
    </div>
  </div>
);

const StatCard = ({ label, value, tone = 'text-gray-900' }) => (
  <div className="card p-4">
    <p className="text-sm font-medium text-gray-500">{label}</p>
    <p className={`mt-1 text-2xl font-semibold ${tone}`}>{value}</p>
  </div>
);

const Office = () => {
  const { canAccess, hasRole } = useAuth();
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [customCategories, setCustomCategories] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });

  const canManageOffice = hasRole(['admin', 'accounts']);
  const canApproveOffice = hasRole(['admin', 'director']);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm({ defaultValues: emptyOfficeExpense });

  const {
    register: registerCategory,
    handleSubmit: handleCategorySubmit,
    reset: resetCategory,
    formState: { errors: categoryErrors, isSubmitting: isSavingCategory }
  } = useForm({ defaultValues: emptyCategory });

  const categoryOptions = useMemo(() => {
    const names = [
      ...officeCategories,
      ...customCategories.map((category) => category.name),
      ...expenses.map((expense) => expense.category)
    ].filter(Boolean);

    return [...new Set(names)].sort((a, b) => a.localeCompare(b));
  }, [customCategories, expenses]);

  const statusCounts = useMemo(() => {
    return expenses.reduce(
      (acc, expense) => {
        acc[expense.status] = (acc[expense.status] || 0) + 1;
        return acc;
      },
      { pending: 0, approved: 0, rejected: 0, paid: 0 }
    );
  }, [expenses]);

  const categoriesInUse = useMemo(() => {
    const counts = {};
    expenses.forEach((expense) => {
      counts[expense.category] = (counts[expense.category] || 0) + Number(expense.amount || 0);
    });
    return Object.entries(counts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3);
  }, [expenses]);

  const fetchExpenses = useCallback(async () => {
    if (!canAccess('office')) return;

    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '100' });
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await api.get(`/api/office/expenses?${params.toString()}`);
      setExpenses(response.data.expenses || []);
      setTotalAmount(response.data.totalAmount || 0);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch office expenses');
    } finally {
      setLoading(false);
    }
  }, [canAccess, filters]);

  const fetchCategories = useCallback(async () => {
    if (!canAccess('office')) return;

    try {
      const response = await api.get('/api/office/categories');
      setCustomCategories(response.data.categories || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch office categories');
    }
  }, [canAccess]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const openModal = async () => {
    await fetchCategories();
    reset(emptyOfficeExpense);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const openCategoryModal = () => {
    resetCategory(emptyCategory);
    setShowCategoryModal(true);
  };

  const closeCategoryModal = () => {
    setShowCategoryModal(false);
  };

  const onSubmit = async (data) => {
    try {
      await api.post('/api/office/expenses', cleanPayload(data));
      toast.success('Office expense created');
      closeModal();
      reset(emptyOfficeExpense);
      fetchExpenses();
    } catch (error) {
      const firstValidation = error.response?.data?.errors?.[0]?.msg;
      toast.error(firstValidation || error.response?.data?.message || 'Failed to create office expense');
    }
  };

  const onCategorySubmit = async (data) => {
    try {
      const response = await api.post('/api/office/categories', cleanPayload(data));
      const category = response.data.category;
      if (category?.name) {
        setCustomCategories((prev) => {
          const exists = prev.some((item) => item._id === category._id || item.name === category.name);
          return exists ? prev : [...prev, category];
        });
        setValue('category', category.name);
      }
      toast.success('Office category created');
      closeCategoryModal();
      resetCategory(emptyCategory);
      await fetchCategories();
    } catch (error) {
      const firstValidation = error.response?.data?.errors?.[0]?.msg;
      toast.error(firstValidation || error.response?.data?.message || 'Failed to create office category');
      await fetchCategories();
    }
  };

  const handleApprove = async (expenseId) => {
    try {
      await api.put(`/api/office/expenses/${expenseId}/approve`);
      toast.success('Office expense approved');
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve office expense');
    }
  };

  const handleReject = async (expenseId) => {
    const comment = window.prompt('Reason for rejection');
    if (!comment?.trim()) return;

    try {
      await api.put(`/api/office/expenses/${expenseId}/reject`, { comment });
      toast.success('Office expense rejected');
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject office expense');
    }
  };

  const handlePay = async (expenseId) => {
    try {
      await api.put(`/api/office/expenses/${expenseId}/pay`);
      toast.success('Office expense marked as paid');
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to mark office expense paid');
    }
  };

  const resetFilters = () => {
    setFilters({ search: '', category: '', status: '', dateFrom: '', dateTo: '' });
  };

  const statusBadge = (status) => {
    switch (status) {
      case 'approved':
        return 'status-approved';
      case 'paid':
        return 'status-paid';
      case 'rejected':
        return 'status-rejected';
      default:
        return 'status-pending';
    }
  };

  if (!canAccess('office')) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">You don't have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Office Expenses</h1>
          <p className="text-gray-600">Track internal operating costs in one office expense register</p>
        </div>
        {canManageOffice && (
          <div className="flex flex-col gap-2 sm:flex-row">
            <button onClick={openCategoryModal} className="btn-secondary flex items-center justify-center">
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Category
            </button>
            <button onClick={openModal} className="btn-primary flex items-center justify-center">
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Expense
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard label="Filtered Spend" value={formatCurrency(totalAmount)} tone="text-red-600" />
        <StatCard label="Expense Records" value={expenses.length} />
        <StatCard label="Pending" value={statusCounts.pending || 0} tone="text-yellow-600" />
        <StatCard label="Paid" value={statusCounts.paid || 0} tone="text-blue-600" />
      </div>

      <div className="card p-4">
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-4">
          <div className="lg:col-span-2 relative">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={filters.search}
              onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
              className="input-field !pl-11"
              placeholder="Search purpose, description, vendor"
            />
          </div>
          <select
            value={filters.category}
            onChange={(event) => setFilters((prev) => ({ ...prev, category: event.target.value }))}
            className="input-field"
          >
            <option value="">All categories</option>
            {categoryOptions.map((category) => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
          <select
            value={filters.status}
            onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
            className="input-field"
          >
            <option value="">All status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="paid">Paid</option>
          </select>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))}
            className="input-field"
          />
          <div className="flex gap-2">
            <input
              type="date"
              value={filters.dateTo}
              onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))}
              className="input-field"
            />
            <button onClick={resetFilters} className="btn-secondary flex items-center justify-center" title="Reset filters">
              <ArrowPathIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        {categoriesInUse.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {categoriesInUse.map(([category, amount]) => (
              <span key={category} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                {category}: {formatCurrency(amount)}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expense</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center">
                    <div className="spinner mx-auto"></div>
                  </td>
                </tr>
              ) : expenses.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">No office expenses found</td>
                </tr>
              ) : (
                expenses.map((expense) => (
                  <tr key={expense._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{expense.purpose}</div>
                      <div className="text-sm text-gray-500 max-w-md truncate">{expense.description}</div>
                      {expense.notes && <div className="text-xs text-gray-400 max-w-md truncate">{expense.notes}</div>}
                      <div className="text-xs text-gray-500 mt-1">Billed by {actorLabel(expense.billedBy)}</div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <button
                        type="button"
                        onClick={() => navigate(`/office/categories/${encodeURIComponent(expense.category)}`)}
                        className="text-primary-600 hover:text-primary-900 font-medium"
                      >
                        {expense.category}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{expense.vendor || 'N/A'}</div>
                      <div className="text-xs text-gray-500">{expense.receiptNumber || 'No receipt'}</div>
                    </td>
                    <td className="px-6 py-4 text-right font-semibold text-gray-900">{formatCurrency(expense.amount)}</td>
                    <td className="px-6 py-4">
                      <span className={statusBadge(expense.status)}>{expense.status}</span>
                      <div className="text-xs text-gray-500 mt-1">{expense.paymentMethod?.replace('_', ' ') || 'cash'}</div>
                      {expense.approvedBy?.name && (
                        <div className="text-xs text-gray-500 mt-1">Approved by {actorLabel(expense.approvedBy)}</div>
                      )}
                      {expense.paidBy?.name && (
                        <div className="text-xs text-gray-500 mt-1">Paid by {actorLabel(expense.paidBy)}</div>
                      )}
                      {expense.referenceNumber && (
                        <div className="text-xs text-gray-500 mt-1">Ref: {expense.referenceNumber}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{formatDate(expense.date)}</div>
                      <div className="text-xs text-gray-500">Created {formatDate(expense.createdAt)}</div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end space-x-2">
                        <button
                          type="button"
                          onClick={() => navigate(`/office/categories/${encodeURIComponent(expense.category)}`)}
                          className="text-primary-600 hover:text-primary-900"
                          title="View category statement"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        {canApproveOffice && expense.status === 'pending' && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleApprove(expense._id)}
                              className="text-green-600 hover:text-green-900"
                              title="Approve"
                            >
                              <CheckIcon className="h-5 w-5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleReject(expense._id)}
                              className="text-red-600 hover:text-red-900"
                              title="Reject"
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          </>
                        )}
                        {canManageOffice && expense.status === 'approved' && (
                          <button
                            type="button"
                            onClick={() => handlePay(expense._id)}
                            className="btn-success text-xs px-2 py-1"
                          >
                            Mark Paid
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <ModalShell title="Add Office Expense" onClose={closeModal}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Category</label>
                <select {...register('category', { required: 'Category is required' })} className="input-field">
                  <option value="">Select category</option>
                  {categoryOptions.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>}
              </div>
              <div>
                <label className="label">Amount</label>
                <input
                  {...register('amount', { required: 'Amount is required', min: { value: 0.01, message: 'Amount must be positive' } })}
                  type="number"
                  step="0.01"
                  className="input-field"
                />
                {errors.amount && <p className="mt-1 text-sm text-red-600">{errors.amount.message}</p>}
              </div>
              <div>
                <label className="label">Purpose</label>
                <input {...register('purpose', { required: 'Purpose is required' })} className="input-field" />
                {errors.purpose && <p className="mt-1 text-sm text-red-600">{errors.purpose.message}</p>}
              </div>
              <div>
                <label className="label">Date</label>
                <input {...register('date', { required: 'Date is required' })} type="date" className="input-field" />
              </div>
              <div>
                <label className="label">Vendor</label>
                <input {...register('vendor')} className="input-field" />
              </div>
              <div>
                <label className="label">Receipt Number</label>
                <input {...register('receiptNumber')} className="input-field" />
              </div>
              <div>
                <label className="label">Payment Method</label>
                <select {...register('paymentMethod')} className="input-field">
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="check">Check</option>
                  <option value="card">Card</option>
                </select>
              </div>
            </div>
            <div>
              <label className="label">Description</label>
              <textarea {...register('description', { required: 'Description is required' })} rows={3} className="input-field" />
              {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>}
            </div>
            <div>
              <label className="label">Notes</label>
              <textarea {...register('notes')} rows={2} className="input-field" />
            </div>
            <div className="flex justify-end space-x-3">
              <button type="button" onClick={closeModal} className="btn-secondary">Cancel</button>
              <button type="submit" disabled={isSubmitting} className="btn-primary">
                {isSubmitting ? 'Saving...' : 'Create Expense'}
              </button>
            </div>
          </form>
        </ModalShell>
      )}

      {showCategoryModal && (
        <ModalShell title="Add Office Category" onClose={closeCategoryModal}>
          <form onSubmit={handleCategorySubmit(onCategorySubmit)} className="space-y-4">
            <div>
              <label className="label">Category Name</label>
              <input
                {...registerCategory('name', { required: 'Category name is required' })}
                className="input-field"
                placeholder="Example: Software Subscription"
              />
              {categoryErrors.name && <p className="mt-1 text-sm text-red-600">{categoryErrors.name.message}</p>}
            </div>
            <div>
              <label className="label">Description</label>
              <textarea {...registerCategory('description')} rows={3} className="input-field" />
            </div>
            <div className="flex justify-end space-x-3">
              <button type="button" onClick={closeCategoryModal} className="btn-secondary">Cancel</button>
              <button type="submit" disabled={isSavingCategory} className="btn-primary">
                {isSavingCategory ? 'Saving...' : 'Create Category'}
              </button>
            </div>
          </form>
        </ModalShell>
      )}
    </div>
  );
};

export default Office;
